import { SVGProps } from 'react';

import { BUS_GROUNDING_ICON_PATHS } from './bus-grounding-icon-paths';
import { IconName } from './icon-names';

export type PathDataObject = Pick<
    SVGProps<SVGPathElement>,
    'd' | 'fill' | 'fillRule' | 'clipRule' | 'stroke' | 'strokeWidth' | 'strokeLinecap' | 'strokeLinejoin'
> & {
    d: string;
    fillRule?: CanvasFillRule; // for canvas
    strokeWidth?: number; // for canvas
    keepUpright?: boolean;
};

export type PathData = string | PathDataObject;

export interface IconPathData {
    paths: PathData[];
    viewBox: string;
    fillsCell?: boolean;
}

const CONVERTER_INPUT_LABEL_PATHS: PathData[] = [
    {
        d: 'M13.62 9.317h1.066a1.881 1.881 0 0 0-.198-.696 1.633 1.633 0 0 0-.416-.514 1.779 1.779 0 0 0-.592-.32 2.332 2.332 0 0 0-.733-.11 2.13 2.13 0 0 0-1.05.26 1.879 1.879 0 0 0-.744.76c-.183.334-.275.741-.275 1.221 0 .479.09.885.27 1.219.18.334.427.588.738.763.312.173.666.26 1.06.26.3 0 .566-.046.798-.137.233-.09.43-.213.592-.366.162-.155.289-.326.381-.514a1.62 1.62 0 0 0 .169-.57l-1.066-.007a.859.859 0 0 1-.1.283.72.72 0 0 1-.434.341c-.096.03-.202.045-.318.045a.973.973 0 0 1-.539-.147.97.97 0 0 1-.352-.441 1.885 1.885 0 0 1-.123-.729c0-.278.04-.515.121-.711a.992.992 0 0 1 .352-.45.954.954 0 0 1 .547-.156c.12 0 .228.018.326.052a.722.722 0 0 1 .433.369.924.924 0 0 1 .087.295Z',
    },
    {
        d: 'M7.955 12.1H6.342V7.736h1.61c.445 0 .828.088 1.15.262.322.174.57.424.745.75.175.326.262.715.262 1.168 0 .455-.087.845-.262 1.172a1.791 1.791 0 0 1-.744.752c-.322.173-.705.26-1.148.26Zm-.558-.9h.517c.245 0 .451-.04.62-.123a.817.817 0 0 0 .386-.407c.088-.189.132-.44.132-.754s-.044-.564-.132-.75a.815.815 0 0 0-.39-.405 1.422 1.422 0 0 0-.63-.125h-.503V11.2Z',
        fillRule: 'evenodd',
        clipRule: 'evenodd',
    },
];

const CONVERTER_OUTPUT_LABEL_PATH: PathData = {
    d: 'M19.68 22.998v.763h-2.913v-.763h2.913Zm4.047-2.783H22.66a.924.924 0 0 0-.087-.296.72.72 0 0 0-.433-.369.987.987 0 0 0-.325-.05.954.954 0 0 0-.548.155.992.992 0 0 0-.352.45c-.08.195-.121.433-.121.711 0 .29.041.533.123.729a.971.971 0 0 0 .352.44.973.973 0 0 0 .54.148c.116 0 .221-.015.317-.045a.719.719 0 0 0 .435-.341.859.859 0 0 0 .1-.283l1.065.006a1.616 1.616 0 0 1-.169.571 1.83 1.83 0 0 1-.381.514c-.162.153-.36.275-.592.366a2.172 2.172 0 0 1-.797.137 2.15 2.15 0 0 1-1.061-.26 1.872 1.872 0 0 1-.738-.763c-.18-.334-.27-.74-.27-1.219 0-.48.091-.887.275-1.22.183-.335.43-.588.743-.762a2.13 2.13 0 0 1 1.05-.26c.265 0 .51.037.734.111a1.8 1.8 0 0 1 .592.32c.17.14.309.31.416.514.106.203.172.435.198.696Z',
};

const CONVERTER_OUTLINE_PATH: PathData = {
    d: 'M3 4a1 1 0 0 1 1-1h22a1 1 0 0 1 1 1v22a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4Zm1.5 20.453V4.5h19.925L4.5 24.453ZM5.575 25.5H25.5V5.547L5.575 25.5Z',
    fillRule: 'evenodd',
    clipRule: 'evenodd',
};

const CONVERTER_UNDERVOLTAGE_ARROW_PATH = 'M19.4 13.2h3.2v5.4h-3.2zM17.2 18.1h7.6l-3.8 4z';

const CONVERTER_UNDERVOLTAGE_LIMIT_PATH = 'M17.2 23h7.6v1.5h-7.6z';

export const ICON_PATHS: Partial<Record<IconName, IconPathData>> = {
    ...BUS_GROUNDING_ICON_PATHS,
    battery: {
        viewBox: '0 0 30 30',
        paths: [
            { d: 'M14.172 14.012 15.151 9 11 16.014h1.981L12.014 21 16 14.012h-1.828Z' },
            {
                d: 'M28 8a1 1 0 0 0-1-1H1a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h26a1 1 0 0 0 1-1v-3h1a1 1 0 0 0 1-1v-6a1 1 0 0 0-1-1h-1V8Zm-1.5.5h-25v13h25v-13Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    breaker: {
        viewBox: '0 0 30 30',
        paths: [
            'M23.226 15.536a.748.748 0 0 0 .524.214H30v-1.5h-5.95L9.83.03l-1.061 1.06L23.132 15.454a.905.905 0 0 0 .094.082Zm-15.766-1.714 2.946-2.947 1.178 1.179L8.638 15l2.946 2.946-1.178 1.179-2.947-2.947-2.946 2.947-1.179-1.179 2.197-2.196H0v-1.5h5.53l-2.196-2.196 1.179-1.179 2.946 2.947Z',
        ],
    },
    breakerClosed: {
        viewBox: '0 0 30 30',
        paths: [
            'M0 14.25h30v1.5H0z',
            'M7.46 13.822l2.946-2.947 1.178 1.179L8.638 15l2.946 2.946-1.178 1.179-2.947-2.947-2.946 2.947-1.179-1.179L6.281 15l-2.947-2.946 1.179-1.179 2.946 2.947Z',
        ],
    },
    breakerUnknown: {
        viewBox: '0 0 30 30',
        paths: [
            'M0 14.25h3.5v1.5H0zM5.3 14.25h3.5v1.5H5.3zM10.6 14.25h3.5v1.5h-3.5zM15.9 14.25h3.5v1.5h-3.5zM21.2 14.25h3.5v1.5h-3.5zM26.5 14.25h3.5v1.5h-3.5z',
            'M7.46 13.822l2.946-2.947 1.178 1.179L8.638 15l2.946 2.946-1.178 1.179-2.947-2.947-2.946 2.947-1.179-1.179L6.281 15l-2.947-2.946 1.179-1.179 2.946 2.947Z',
        ],
    },
    bus: {
        viewBox: '0 0 30 30',
        paths: ['M0 13h30v4H0z'],
    },
    cable: {
        viewBox: '0 0 30 30',
        paths: ['M0 13h30v4H0z'],
    },
    capacitor: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M11 6h1.5v18H11v-8.25H0v-1.5h11V6Zm19 8.25H19V6h-1.5v18H19v-8.25h11v-1.5Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    charger: {
        viewBox: '0 0 30 30',
        paths: [
            'M2 0h1.5v3h3V0H8v3h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H5.5v14.5h21v-21H12V3h15a1 1 0 0 1 1 1v22a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V11H1a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h1V0Z',
            'M10.717 11.727V19h4.759v-1.104h-3.441V15.91h3.167v-1.104h-3.167v-1.974h3.412v-1.105h-4.73Zm8.94 5.725-1.892-5.725h-1.446L18.88 19h1.627l2.564-7.273H21.62l-1.89 5.725h-.074Z',
        ],
    },
    combinerBox: {
        viewBox: '0 0 30 30',
        paths: ['M0 13h30v4H0z'],
    },
    contactor: {
        viewBox: '0 0 30 30',
        paths: [
            { d: 'M15.75 0v6.5H25V8H5V6.5h9.25V0h1.5Z' },
            {
                d: 'M8 12a3 3 0 1 1-2.905 3.75H0v-1.5h5.095A3.001 3.001 0 0 1 8 12Zm-1.5 3a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM22 12a3 3 0 0 1 2.905 2.25H30v1.5h-5.095A3.001 3.001 0 0 1 19 15a3 3 0 0 1 3-3Zm0 1.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    connector: {
        viewBox: '0 0 24 24',
        paths: [
            {
                d: 'M9.785 6 18 14.215l-2.054 2.054a5.81 5.81 0 1 1-8.215-8.215L9.785 6M4 20l3.5-3.5M15 4l-3.5 3.5M20 9l-3.5 3.5',
                stroke: 'currentColor',
                strokeWidth: 1.25,
                fill: 'none',
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
            },
        ],
    },
    controller: {
        viewBox: '0 0 24 24',
        paths: [
            { d: 'M0 0h24v24H0z', fill: 'none', stroke: 'none' },
            {
                d: 'M4 10a2 2 0 1 0 4 0 2 2 0 0 0-4 0M6 4v4M6 12v8M10 16a2 2 0 1 0 4 0 2 2 0 0 0-4 0M12 4v10M12 18v2M16 7a2 2 0 1 0 4 0 2 2 0 0 0-4 0M18 4v1M18 9v11',
                stroke: 'currentColor',
                strokeWidth: 1.25,
                fill: 'none',
            },
        ],
    },
    converter: {
        viewBox: '0 0 30 30',
        paths: [...CONVERTER_INPUT_LABEL_PATHS, CONVERTER_OUTPUT_LABEL_PATH, CONVERTER_OUTLINE_PATH],
    },
    converterUndervoltage: {
        viewBox: '0 0 30 30',
        paths: [
            ...CONVERTER_INPUT_LABEL_PATHS,
            CONVERTER_OUTLINE_PATH,
            CONVERTER_UNDERVOLTAGE_ARROW_PATH,
            CONVERTER_UNDERVOLTAGE_LIMIT_PATH,
        ],
    },
    converterUnknown: {
        viewBox: '0 0 30 30',
        paths: [...CONVERTER_INPUT_LABEL_PATHS, CONVERTER_OUTLINE_PATH, CONVERTER_UNDERVOLTAGE_ARROW_PATH],
    },
    custom: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M3 4a1 1 0 0 1 1-1h22a1 1 0 0 1 1 1v22a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4Z',
                stroke: 'currentColor',
                strokeWidth: 2,
                fill: 'none',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    diode: {
        viewBox: '0 0 24 24',
        paths: [
            {
                d: 'M22 12h-6M2 12h6M8 7l8 5-8 5zM16 7v10',
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 1.25,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
            },
        ],
    },
    disconnect: {
        viewBox: '0 0 30 30',
        paths: [
            'M6.25 15.75C6.45401 15.75 6.639 15.6685 6.77421 15.5364C6.80666 15.5111 6.83791 15.4835 6.86774 15.4537L21.231 1.09045L20.1703 0.0297852L5.95014 14.25H0V15.75H6.25Z',
            'M30 14.25H23V15.75H30V14.25Z',
        ],
    },
    efuseBreaker: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M5 14.25H0v1.5h5V21a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-5.25h5v-1.5h-5V9a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v5.25ZM6.5 9.5h17v4.75H20v1.5h3.5v4.75h-17V15.75H10v-1.5H6.5V9.5Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
            {
                d: 'M12 11h6v1.5h-4.5v1.75H17v1.5h-3.5V17.5H18V19h-6V11Z',
                keepUpright: true,
            },
        ],
    },
    efuseBreakerCleared: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M5 14.25H0v1.5h5V21a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-5.25h5v-1.5h-5V9a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v5.25ZM6.5 9.5h17v11h-17z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
            {
                d: 'M12 11h6v1.5h-4.5v1.75H17v1.5h-3.5V17.5H18V19h-6V11Z',
                keepUpright: true,
            },
        ],
    },
    efuseBreakerUnknown: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M5 14.25H0v1.5h5V21a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-5.25h5v-1.5h-5V9a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v5.25ZM6.5 9.5h17v11h-17z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
            'M6.5 14.25H8v1.5H6.5zM8.5 14.25H10v1.5H8.5zM20 14.25h1.5v1.5H20zM22 14.25h1.5v1.5H22z',
            {
                d: 'M12 11h6v1.5h-4.5v1.75H17v1.5h-3.5V17.5H18V19h-6V11Z',
                keepUpright: true,
            },
        ],
    },
    fuelCell: {
        viewBox: '0 0 24 24',
        paths: [
            { d: 'M0 0h24v24H0z', stroke: 'none', fill: 'none' },
            {
                d: 'M7.502 19.423c2.602 2.105 6.395 2.105 8.996 0 2.602-2.105 3.262-5.708 1.566-8.546l-4.89-7.26c-.42-.625-1.287-.803-1.936-.397a1.376 1.376 0 0 0-.41.397l-4.893 7.26C4.24 13.715 4.9 17.318 7.502 19.423z',
                stroke: 'currentColor',
                strokeWidth: 1.25,
                fill: 'none',
            },
        ],
    },
    fuse: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M5 14.25H0v1.5h5V21a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-5.25h5v-1.5h-5V9a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v5.25Zm1.5 0V9.5h17v4.75h-17Zm17 1.5v4.75h-17v-4.75h17Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    fuseCleared: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M5 14.25H0v1.5h5V21a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-5.25h5v-1.5h-5V9a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v5.25ZM6.5 9.5h17v11h-17z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    fuseUnknown: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M5 14.25H0v1.5h5V21a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-5.25h5v-1.5h-5V9a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v5.25ZM6.5 9.5h17v11h-17z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
            'M6.5 14.25h3.5v1.5H6.5zM11 14.25h3.5v1.5H11zM15.5 14.25h3.5v1.5h-3.5zM20 14.25h3.5v1.5H20z',
        ],
    },
    generator: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M16.995 12.942c.125.181.223.387.294.618h1.61a3.244 3.244 0 0 0-.412-1.177 3.326 3.326 0 0 0-.806-.92 3.58 3.58 0 0 0-1.112-.597 4.167 4.167 0 0 0-1.35-.213c-.572 0-1.1.103-1.586.307a3.59 3.59 0 0 0-1.27.886 4.11 4.11 0 0 0-.844 1.411c-.199.554-.298 1.183-.298 1.888 0 .92.169 1.716.507 2.386a3.745 3.745 0 0 0 1.42 1.547c.607.36 1.313.541 2.117.541.722 0 1.36-.146 1.918-.439a3.26 3.26 0 0 0 1.312-1.261c.318-.546.477-1.192.477-1.939v-1.048h-3.54v1.236h2.01a2.21 2.21 0 0 1-.264 1.052 1.801 1.801 0 0 1-.754.725c-.324.17-.707.255-1.15.255-.49 0-.918-.118-1.287-.354-.37-.238-.658-.586-.866-1.044-.207-.457-.31-1.015-.31-1.674 0-.654.103-1.208.31-1.662.21-.458.5-.804.866-1.04a2.265 2.265 0 0 1 1.252-.354c.262 0 .503.034.725.103.221.065.417.163.588.294a1.9 1.9 0 0 1 .443.473Z',
            },
            {
                d: 'M3.023 14.25C3.41 7.972 8.624 3 15 3c6.375 0 11.59 4.972 11.977 11.25H30v1.5h-3.023C26.59 22.028 21.375 27 15 27 8.624 27 3.41 22.028 3.023 15.75H0v-1.5h3.023ZM15 25.5C9.201 25.5 4.5 20.799 4.5 15S9.201 4.5 15 4.5 25.5 9.201 25.5 15 20.799 25.5 15 25.5Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    grounding: {
        viewBox: '0 0 24 24',
        paths: [
            {
                d: 'M12 13V5M4 13h16M7 16h10M10 19h4',
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 1.25,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
            },
        ],
    },
    hvac: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M2 23.5v.5h26V4H2v19.5Zm11.22-3.555c.08.102.278.35.679.412.265.057.554.02.771-.025a2.82 2.82 0 0 0 .613-.204c1.723-.728 2.885-2.057 3.01-3.712v-.015c.067-1.483-.52-2.366-1.156-2.861-.797-.62-1.847-.848-2.925-.706A2.509 2.509 0 0 0 12.98 11.7c.45-.891 1.167-1.794 1.725-2.252a.793.793 0 0 0 .392-.643l-.499-.03.5.03v-.003c.007-.13.026-.446-.231-.76-.159-.219-.399-.385-.591-.496a2.794 2.794 0 0 0-.592-.257c-1.77-.608-3.521-.394-4.719.755l-.005.005-.005.005c-1.04 1.059-1.193 2.11-1.05 2.903.177.978.79 1.837 1.666 2.45a2.53 2.53 0 0 0-.07.504c-.98-.152-2.052-.55-2.658-.935a.793.793 0 0 0-.733-.173l-.003.001c-.125.033-.433.114-.65.456-.16.219-.242.499-.287.716a2.793 2.793 0 0 0-.06.642c-.028 1.871.72 3.47 2.185 4.25l.007.003.007.004c1.33.658 2.375.476 3.085.093.913-.492 1.553-1.396 1.847-2.48.214-.02.42-.067.614-.138.266.967.337 2.132.226 2.852a.793.793 0 0 0 .137.74l.002.003Zm-6.144-4.191a4.87 4.87 0 0 1-.117-.604c.975.39 2.157.68 3.165.715.093.095.19.18.294.256-.17.62-.508 1.076-.878 1.272-.37.166-.796.183-1.348-.09a2.547 2.547 0 0 1-1.116-1.55Zm4.917-6.95c.196.012.404.038.61.077-.675.806-1.318 1.838-1.665 2.786a2.28 2.28 0 0 0-.335.2c-.536-.356-.864-.819-.935-1.231-.043-.403.074-.813.506-1.253a2.547 2.547 0 0 1 1.819-.579Zm2.411 6.232a2.46 2.46 0 0 0 .117-.372c.636-.094 1.189.032 1.517.291.301.271.488.655.46 1.27-.1.63-.4 1.195-.968 1.646a4.89 4.89 0 0 1-.505.35c-.036-1.05-.246-2.248-.62-3.185ZM1.5 4a.5.5 0 0 1 .5-.5h26a.5.5 0 0 1 .5.5v20a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5V4Zm19 17.5v-1h5v1h-5Zm-1-7.5a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Zm-7 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z',
                fill: 'currentColor',
                stroke: 'currentColor',
            },
        ],
    },
    hydro: {
        viewBox: '0 0 30 30',
        paths: [
            { d: 'M2.5 27h25v3h-25v-3Z' },
            {
                d: 'M17 4.352v.376l.361.104a8.452 8.452 0 0 1 2.071.914l.318.194.277-.249c.607-.544 1.534-1.143 2.53-1.362.838-.185 1.72-.103 2.541.523L22.2 7.75l-.286.287.22.34A8.452 8.452 0 0 1 23.3 11.16l.076.344.35.044c.825.104 1.786.384 2.528.932.615.454 1.088 1.096 1.213 2.02h-4.09l-.088.39a8.436 8.436 0 0 1-.75 2.04l-.163.311.24.259c.547.592 1.17 1.527 1.407 2.54.2.854.127 1.756-.51 2.594l-2.775-2.775-.306-.306-.347.26a8.47 8.47 0 0 1-2.758 1.365l-.325.092-.036.335c-.089.827-.357 1.82-.906 2.593-.457.644-1.11 1.14-2.06 1.27v-4.016l-.417-.07a8.452 8.452 0 0 1-3.507-1.453l-.304-.216-.294.23c-.655.511-1.535.997-2.45 1.138-.76.117-1.553-.001-2.298-.569l2.766-2.766.268-.267-.185-.33a8.44 8.44 0 0 1-.88-2.309l-.075-.344-.35-.044c-.825-.104-1.786-.384-2.528-.932-.616-.454-1.088-1.096-1.213-2.02h4.09l.088-.39c.215-.95.59-1.839 1.094-2.639l.205-.324-.26-.282c-.548-.592-1.172-1.527-1.41-2.54-.2-.854-.126-1.757.512-2.595l3.003 3.004.282.282.338-.213a8.444 8.444 0 0 1 3.17-1.196l.364-.058.051-.366c.115-.82.404-1.758.95-2.477.452-.595 1.086-1.05 1.99-1.173v3.819Zm-3.067 8.993 1.897-2.85-.346 1.575-.133.607h1.419l-1.738 2.71.313-1.435.132-.607h-1.544ZM15 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z',
                fill: 'currentColor',
                stroke: 'currentColor',
            },
        ],
    },
    light: {
        viewBox: '0 0 30 30',
        paths: [
            'M5 6.25h20v1.5H5v-1.5Zm-3 4h26v1.5H2v-1.5Zm3.078 5.318-.964-1.149-3.192 2.678.964 1.15 3.192-2.679Zm2.836 2.652-2.678 3.19-1.15-.964 2.679-3.192 1.149.964Zm3.526 1.412-1.409-.513-1.425 3.915 1.41.513 1.425-3.915Zm4.357.618v4.167h-1.5V20.25h1.5Zm4.242-1.131-1.41.513 1.425 3.915 1.41-.513-1.425-3.915Zm3.243-1.864 2.678 3.192-1.149.964-2.678-3.192 1.149-.964Zm2.651-2.835-.965 1.149 3.192 2.678.965-1.15-3.192-2.678Z',
        ],
    },
    load: {
        viewBox: '0 0 30 30',
        paths: [
            { d: 'M10 2.75h10v2.5H10v-2.5Zm0 4h10v2.5H10v-2.5Z' },
            {
                d: 'M20 15h5.151c.882 0 1.332 1.058.721 1.693L15.721 27.25a1 1 0 0 1-1.442 0L4.128 16.693c-.611-.635-.16-1.693.72-1.693H10v-3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3Zm-8.5 1.5v-4h7v4h5.477L15 25.836 6.023 16.5H11.5Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    meter: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M3 4a1 1 0 0 1 1-1h22a1 1 0 0 1 1 1v22a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4Zm1.5 6.5v-6h21v6h-21Zm21 1.5v13.5h-21V12h21Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    motor: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M12.296 10.773H10.36V19.5h1.517v-6.008h.08l2.413 5.982h1.133l2.412-5.97h.081V19.5h1.517v-8.727H17.58l-2.591 6.324h-.103l-2.59-6.324Z',
            },
            {
                d: 'M3.041 14C3.55 7.84 8.71 3 15 3s11.45 4.84 11.959 11H30v2h-3.041C26.45 22.16 21.29 27 15 27S3.55 22.16 3.041 16H0v-2h3.041ZM15 25.5C9.201 25.5 4.5 20.799 4.5 15S9.201 4.5 15 4.5 25.5 9.201 25.5 15 20.799 25.5 15 25.5Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    panel: {
        viewBox: '0 0 30 30',
        paths: [
            { d: 'M22 24H8v1.5h14V24ZM8 21h14v1.5H8V21Z' },
            {
                d: 'm15 3 8.66 15.75H6.34L15 3Zm1.32 6-.782 3.341H17L13.811 17l.774-3.324H13L16.32 9Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
            {
                d: 'M4 1a1 1 0 0 1 1-1h20a1 1 0 0 1 1 1v28a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V1Zm1.5 27.5v-27h19v27h-19Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    powerDistributionUnit: {
        viewBox: '0 0 30 30',
        paths: ['M0 13h30v4H0z'],
    },
    precharge: {
        viewBox: '0 0 24 24',
        paths: [
            { d: 'M0 0h24v24H0z', stroke: 'none', fill: 'none' },
            {
                d: 'M7.038 4.5a9 9 0 0 0-2.495 2.47M3.186 10.209a9 9 0 0 0 0 3.508M4.5 16.962a9 9 0 0 0 2.47 2.495M10.209 20.814a9 9 0 0 0 3.5 0M16.962 19.5a9 9 0 0 0 2.495-2.47M20.814 13.791a9 9 0 0 0 0-3.508M19.5 7.038a9 9 0 0 0-2.47-2.495M13.791 3.186a9 9 0 0 0-3.508-.02M12 8l-2 4h4l-2 4',
                stroke: 'currentColor',
                strokeWidth: 1.25,
                fill: 'none',
            },
            {
                d: 'M12 21a9 9 0 0 0 0-18',
                stroke: 'currentColor',
                strokeWidth: 1.25,
                fill: 'none',
            },
        ],
    },
    reference: {
        viewBox: '0 0 24 24',
        paths: [
            {
                stroke: 'currentColor',
                strokeWidth: 1.25,
                fill: 'none',
                d: 'm9 15 6-6M11 6l.463-.536a5 5 0 0 1 7.071 7.072L18 13M13 18l-.397.534a5.068 5.068 0 0 1-7.127 0 4.972 4.972 0 0 1 0-7.071L6 11',
            },
        ],
    },
    software: {
        viewBox: '0 0 24 24',
        paths: [
            'M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM7 20h10M9 16v4M15 16v4M9 12V8M12 12v-1M15 12v-2M12 12v-1',
        ],
    },
    solar: {
        viewBox: '0 0 30.4 30.4',
        paths: [
            {
                d: 'M15.75-.083v4.166h-1.5V-.083h1.5ZM5.306 4.545l-.76 1.293 3.72 2.189.76-1.293-3.72-2.189ZM20 11a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z',
            },
            {
                d: 'M5 17h20l5 13H0l5-13Zm-.547 5.6 1.577-4.1h5.195l-.954 4.1H4.454Zm-.577 1.5-1.692 4.4H8.9l1.023-4.4H3.876Zm7.587 0-1.023 4.4h9.434l-1.024-4.4h-7.387Zm8.927 0 1.024 4.4h6.402l-1.692-4.4H20.39Zm5.157-1.5H20.04l-.954-4.1h4.884l1.577 4.1Zm-7.047 0h-6.688l.953-4.1h4.781l.954 4.1Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
            {
                d: 'm25.457 5.839-3.724 2.188-.76-1.294 3.724-2.188.76 1.294ZM2.917 12.25v1.5h4.166v-1.5H2.917Zm20 0v1.5h4.166v-1.5h-4.166Z',
            },
        ],
    },
    solution: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M3 4a1 1 0 0 1 1-1h22a1 1 0 0 1 1 1v22a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4Z m12 -1 v 24 m 12 -12 h-24',
                stroke: 'currentColor',
                strokeWidth: 2,
                fill: 'none',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    transferSwitch: {
        viewBox: '0 0 30 30',
        paths: [
            'M15.75 4.14538C16.483 4.44207 17 5.16066 17 6C17 7.10457 16.1046 8 15 8C13.8954 8 13 7.10457 13 6C13 5.16066 13.517 4.44207 14.25 4.14538V0H15.75V4.14538ZM14.25 30L14.25 23V22.8461L14.3106 22.7046L21.8106 5.20456L23.1894 5.79544L15.75 23.1539L15.75 30H14.25ZM24 17C24.8393 17 25.5579 16.483 25.8546 15.75H30V14.25H25.8546C25.5579 13.517 24.8393 13 24 13C22.8954 13 22 13.8954 22 15C22 16.1046 22.8954 17 24 17ZM24 14.5C23.7239 14.5 23.5 14.7239 23.5 15C23.5 15.2761 23.7239 15.5 24 15.5C24.2761 15.5 24.5 15.2761 24.5 15C24.5 14.7239 24.2761 14.5 24 14.5ZM14.5 6C14.5 5.72386 14.7239 5.5 15 5.5C15.2761 5.5 15.5 5.72386 15.5 6C15.5 6.27614 15.2761 6.5 15 6.5C14.7239 6.5 14.5 6.27614 14.5 6Z',
        ],
    },
    transformer: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M15 21.93a8 8 0 0 1-11.965-6.18H0v-1.5h3.035A8 8 0 0 1 15 8.07a8 8 0 0 1 11.965 6.18H30v1.5h-3.035A8 8 0 0 1 15 21.93Zm-4-.43a6.5 6.5 0 1 1 2.642-12.44A7.98 7.98 0 0 0 11 15a7.98 7.98 0 0 0 2.642 5.94c-.807.36-1.701.56-2.642.56Zm5.358-12.44a6.5 6.5 0 1 1 0 11.881A7.98 7.98 0 0 0 19 15a7.98 7.98 0 0 0-2.642-5.94ZM15 9.875A6.489 6.489 0 0 1 17.5 15a6.489 6.489 0 0 1-2.5 5.124A6.489 6.489 0 0 1 12.5 15 6.49 6.49 0 0 1 15 9.876Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
        ],
    },
    utility: {
        viewBox: '0 0 24 24',
        paths: [
            {
                d: 'M4 12c0-1.657 1.592-3 3.556-3s3.11 1.5 4.444 3c1.333 1.5 2.48 3 4.444 3S20 13.657 20 12',
                stroke: 'currentColor',
                strokeWidth: 1.5,
                fill: 'none',
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
            },
        ],
    },
    wind: {
        viewBox: '0 0 30 30',
        paths: [
            {
                d: 'M15.629 11.1a2.001 2.001 0 0 1 1.345 2.222L26 18c-1.86-.112-5.84-.485-6.88-1.085-.796-.46-1.958-1.584-2.771-2.438a2 2 0 0 1-3.123-.553L4.25 19.665c1.027-1.555 3.34-4.815 4.38-5.415 1.031-.596 3.33-1.165 4.374-1.38a2.001 2.001 0 0 1 1.476-1.802L14 .5c.833 1.667 2.5 5.3 2.5 6.5 0 1.06-.52 2.978-.871 4.1ZM16 13a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            },
            { d: 'M15 15h-.5L13 29.5h4L15.5 15H15Z' },
        ],
    },
};

export const getIconPathData = (name: IconName): IconPathData | undefined => ICON_PATHS[name];
